// SIWS CUSTOM: Leather Key Rings color picker — key-rings-picker.js
// 2 colors (Blue, Black). Min 50 per color, increments of 50.
// Tiered pricing via 2 Shopify variants: "50-99 units" = $3.50, "100+ units" = $3.25.
// JS selects the correct variant ID at ATC time based on combined qty.
// Initialized on DOMContentLoaded for all .key-rings-picker elements.

'use strict';

const SIWS_KEY_RINGS_COLORS = [
  { id: 'blue',  name: 'Blue',  swatch: '#1A56DB' },
  { id: 'black', name: 'Black', swatch: '#111827' },
];

const SIWS_KEY_RINGS_STEP = 50;

function siwsKeyRingsUnitPrice(total) {
  return total >= 100 ? 3.25 : 3.50;
}

class SIWSKeyRingsPicker {
  constructor(el) {
    this.el        = el;
    this.colorQtys = { blue: 0, black: 0 };

    // SIWS CUSTOM: Parse both tier variant IDs from Liquid-emitted JSON.
    // "50-99 units" variant → low-tier price; "100+ units" variant → high-tier price.
    let variants = [];
    try { variants = JSON.parse(el.dataset.variants || '[]'); } catch (e) {}

    const lowV  = variants.find(v => v.title && v.title.includes('50-99'));
    const highV = variants.find(v => v.title && v.title.includes('100'));

    this.variantIdLow  = lowV  ? lowV.id  : null;
    this.variantIdHigh = highV ? highV.id : null;

    this.init();
  }

  init() {
    this._renderColorCards();
    this._bindEvents();
    this._update();
  }

  _totalQty() {
    return Object.values(this.colorQtys).reduce((s, q) => s + q, 0);
  }

  _selectedColors() {
    return SIWS_KEY_RINGS_COLORS.filter(c => (this.colorQtys[c.id] || 0) > 0);
  }

  _increment(id) {
    this.colorQtys[id] = (this.colorQtys[id] || 0) + SIWS_KEY_RINGS_STEP;
    this._update();
  }

  _decrement(id) {
    const current = this.colorQtys[id] || 0;
    if (current <= 0) return;
    this.colorQtys[id] = current - SIWS_KEY_RINGS_STEP;
    this._update();
  }

  _renderColorCards() {
    const container = this.el.querySelector('#key-rings-color-cards');
    if (!container) return;

    const grid = document.createElement('div');
    grid.className = 'screwdriver-color-grid';

    SIWS_KEY_RINGS_COLORS.forEach(color => {
      grid.appendChild(this._buildCardEl(color));
    });

    container.appendChild(grid);
  }

  _buildCardEl(color) {
    const card = document.createElement('div');
    card.className       = 'screwdriver-color-card';
    card.dataset.colorId = color.id;

    const swatchWrap = document.createElement('div');
    swatchWrap.className = 'screwdriver-color-card__swatch-wrap';

    const swatch = document.createElement('span');
    swatch.className        = 'screwdriver-color-card__swatch';
    swatch.style.background = color.swatch;
    swatch.setAttribute('aria-hidden', 'true');

    swatchWrap.appendChild(swatch);

    const name = document.createElement('span');
    name.className   = 'screwdriver-color-card__name';
    name.textContent = color.name;

    const stepper = document.createElement('div');
    stepper.className = 'screwdriver-color-card__stepper';

    const minusBtn = document.createElement('button');
    minusBtn.type            = 'button';
    minusBtn.className       = 'screwdriver-color-card__minus';
    minusBtn.dataset.colorId = color.id;
    minusBtn.textContent     = '\u2212';
    minusBtn.disabled        = true;
    minusBtn.setAttribute('aria-label', `Remove 50 ${color.name}`);

    const qtySpan = document.createElement('span');
    qtySpan.className   = 'screwdriver-color-card__qty';
    qtySpan.textContent = '0';

    const plusBtn = document.createElement('button');
    plusBtn.type            = 'button';
    plusBtn.className       = 'screwdriver-color-card__plus';
    plusBtn.dataset.colorId = color.id;
    plusBtn.textContent     = '+';
    plusBtn.setAttribute('aria-label', `Add 50 ${color.name}`);

    stepper.appendChild(minusBtn);
    stepper.appendChild(qtySpan);
    stepper.appendChild(plusBtn);

    card.appendChild(swatchWrap);
    card.appendChild(name);
    card.appendChild(stepper);

    return card;
  }

  _bindEvents() {
    this.el.addEventListener('click', e => {
      const plusBtn  = e.target.closest('.screwdriver-color-card__plus');
      const minusBtn = e.target.closest('.screwdriver-color-card__minus');
      if (plusBtn)  this._increment(plusBtn.dataset.colorId);
      if (minusBtn) this._decrement(minusBtn.dataset.colorId);
    });

    const atcBtn = this.el.querySelector('#key-rings-atc-btn');
    if (atcBtn) atcBtn.addEventListener('click', () => this._addToCart());
  }

  _update() {
    this._updateCardStates();
    this._updateDiscountMsg();
    this._updateSummary();
    this._updateButton();
  }

  _updateCardStates() {
    this.el.querySelectorAll('.screwdriver-color-card').forEach(card => {
      const id  = card.dataset.colorId;
      const qty = this.colorQtys[id] || 0;

      card.classList.toggle('screwdriver-color-card--selected', qty > 0);

      const minusBtn = card.querySelector('.screwdriver-color-card__minus');
      const qtySpan  = card.querySelector('.screwdriver-color-card__qty');

      if (qtySpan)  qtySpan.textContent = qty;
      if (minusBtn) minusBtn.disabled   = qty === 0;
    });
  }

  _updateDiscountMsg() {
    const msgEl = this.el.querySelector('#key-rings-discount-msg');
    if (!msgEl) return;

    const total = this._totalQty();

    if (total <= 0) {
      msgEl.hidden = true;
      return;
    }

    msgEl.hidden = false;

    if (total < 100) {
      const needed = 100 - total;
      msgEl.style.cssText = 'background:#eff6ff;border:1px solid #3b82f6;border-radius:8px;padding:12px 16px;font-size:0.875em;line-height:1.5;margin:0 0 16px;color:#1e3a5f;';
      msgEl.textContent   = `Add ${needed} more across both colors to unlock $3.25/each pricing.`;
    } else {
      msgEl.style.cssText = 'background:#f0fdf4;border:1px solid #86efac;border-radius:8px;padding:12px 16px;font-size:0.875em;line-height:1.5;margin:0 0 16px;color:#166534;';
      msgEl.textContent   = '\u2705 $3.25/each pricing unlocked \u2014 best value!';
    }
  }

  _updateSummary() {
    const summaryEl = this.el.querySelector('#key-rings-summary');
    if (!summaryEl) return;

    const selected = this._selectedColors();
    if (selected.length === 0) {
      summaryEl.hidden = true;
      return;
    }

    const total      = this._totalQty();
    const unitPrice  = siwsKeyRingsUnitPrice(total);
    const grandTotal = (total * unitPrice).toFixed(2);

    const hStyle = 'text-align:right;padding:0 0 0.6rem;font-size:1.1rem;color:rgba(var(--color-foreground),0.45);font-weight:600;';
    const hStyleL = 'text-align:left;padding:0 0 0.6rem;font-size:1.1rem;color:rgba(var(--color-foreground),0.45);font-weight:600;';

    const rows = selected.map(c => {
      const qty      = this.colorQtys[c.id];
      const lineCost = (qty * unitPrice).toFixed(2);
      const dot      = `<span class="screwdriver-summary__dot" style="background:${c.swatch};"></span>`;

      return `<tr>
        <td>${dot}${c.name}</td>
        <td class="screwdriver-summary__qty-cell">${qty}</td>
        <td class="screwdriver-summary__qty-cell">$${unitPrice.toFixed(2)}</td>
        <td class="screwdriver-summary__qty-cell">$${lineCost}</td>
      </tr>`;
    }).join('');

    summaryEl.innerHTML = `
      <h4 class="screwdriver-summary__title">Adding to Cart</h4>
      <table class="screwdriver-summary__table">
        <thead><tr>
          <th style="${hStyleL}">Color</th>
          <th style="${hStyle}">Qty</th>
          <th style="${hStyle}">Unit Price</th>
          <th style="${hStyle}">Total</th>
        </tr></thead>
        <tbody>${rows}</tbody>
        <tfoot>
          <tr class="screwdriver-summary__total-row">
            <td colspan="3">Grand Total</td>
            <td class="screwdriver-summary__qty-cell">$${grandTotal}</td>
          </tr>
        </tfoot>
      </table>`;
    summaryEl.hidden = false;
  }

  _updateButton() {
    const atcBtn = this.el.querySelector('#key-rings-atc-btn');
    if (!atcBtn) return;

    const total     = this._totalQty();
    const unitPrice = siwsKeyRingsUnitPrice(total);
    const cost      = (total * unitPrice).toFixed(2);

    if (total === 0) {
      atcBtn.disabled    = true;
      atcBtn.textContent = 'Select colors to continue';
    } else {
      atcBtn.disabled    = false;
      atcBtn.textContent = `Add to Cart \u2014 $${cost}`;
    }
  }

  _showError(msg) {
    const el = this.el.querySelector('#key-rings-atc-error');
    if (!el) return;
    el.textContent = msg;
    el.hidden      = false;
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  _hideError() {
    const el = this.el.querySelector('#key-rings-atc-error');
    if (el) el.hidden = true;
  }

  async _addToCart() {
    const atcBtn = this.el.querySelector('#key-rings-atc-btn');
    if (!atcBtn || atcBtn.disabled) return;

    const selected  = this._selectedColors();
    const total     = this._totalQty();

    // SIWS CUSTOM: Line item properties — one entry per selected color + compact breakdown string.
    const properties = { 'Total units': String(total) };
    selected.forEach(c => {
      properties[c.name] = String(this.colorQtys[c.id]);
    });
    properties['_color_breakdown'] = selected
      .map(c => `${c.name}: ${this.colorQtys[c.id]}`)
      .join(', ');

    const originalText = atcBtn.textContent;
    atcBtn.disabled    = true;
    atcBtn.textContent = 'Adding to cart\u2026';
    this._hideError();

    try {
      // SIWS CUSTOM: Select variant by tier — <100 units = $3.50 variant, 100+ = $3.25 variant.
      const variantId = total >= 100 ? this.variantIdHigh : this.variantIdLow;
      if (!variantId) {
        throw new Error('Variant configuration error. Please refresh and try again.');
      }

      // SIWS CUSTOM (2026-05-12): augment body with Section Rendering API hints
      // so the Dawn cart drawer/notification can re-render itself from the response.
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const body = {
        items: [{ id: parseInt(variantId, 10), quantity: total, properties }],
        sections:     cart ? cart.getSectionsToRender().map((s) => s.id) : ['cart-icon-bubble'],
        sections_url: window.location.pathname,
      };

      const res = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body:    JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add to cart.');
      }

      // SIWS CUSTOM (2026-05-12): swap redirect for Dawn cart drawer/notification.
      // Page-mode fallback preserves prior behavior when no cart element exists.
      const responseData = await res.json();
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'siws-key-rings', cartData: responseData });
      }
      if (cart) {
        cart.renderContents(responseData);
      } else {
        // SIWS CUSTOM (2026-05-12): Page-mode — stay on PDP, refresh cart bubble.
        const newBubbleHTML = responseData.sections && responseData.sections['cart-icon-bubble'];
        const liveBubble = document.getElementById('cart-icon-bubble');
        if (newBubbleHTML && liveBubble) {
          const parsed = new DOMParser().parseFromString(newBubbleHTML, 'text/html');
          const inner = parsed.querySelector('.shopify-section');
          if (inner) liveBubble.innerHTML = inner.innerHTML;
        }
        // SIWS CUSTOM (2026-05-12): clear picker state so a re-click can't double-add.
        this.colorQtys = {};
        this._update();
      }

    } catch (err) {
      console.error('[SIWS] Key rings add-to-cart error:', err);
      atcBtn.disabled    = false;
      atcBtn.textContent = originalText;
      this._showError(err.message || 'Something went wrong. Please try again.');
    }
  }
}

function _siwsInitKeyRingsPickers() {
  document.querySelectorAll('.key-rings-picker').forEach(el => {
    new SIWSKeyRingsPicker(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', _siwsInitKeyRingsPickers);
} else {
  _siwsInitKeyRingsPickers();
}
